/**
 * billingEngine.ts — Pure Business Logic (Zero UI Dependencies)
 *
 * Byte-identical port of BigxBookApp/src/billing/billingEngine.ts
 * (the unused `isValidGSTRate` import is dropped to silence the
 * desktop linter; behaviour is unchanged).
 *
 * All monetary arithmetic uses integer "paisa" (×100) internally to
 * eliminate JavaScript floating-point errors (0.1 + 0.2 ≠ 0.3).
 *
 * GST Compliance Features:
 *  - Tax-inclusive pricing (reverse calculation per Section 15)
 *  - Configurable rounding modes (NEAREST/UP/DOWN/NONE)
 *  - TaxProfile resolution chain (TaxProfile → product fallback)
 *  - Composition scheme support (zero tax on invoice)
 */

import {
    reverseCalculateTaxable,
    applyRounding,
    type RoundingMode,
} from '../utils/gstUtils';

// ═══════════════════════════════════════════════════════
//  TYPES
// ═══════════════════════════════════════════════════════

export interface ProductForBilling {
    id: string;
    name: string;
    sellingPrice: number;
    mrp: number;
    gstRate: number;
    cessRate: number;
    hsnCode: string | null;
    unit: string;
    currentStock: number;
    effectiveStock?: number;
    isService: boolean;
    trackInventory: boolean;
    imageUrl: string | null;
    categoryId: string | null;
    barcode: string | null;
    sku: string | null;
    /** Cost/purchase price — used only for the "below cost" soft warning. */
    purchasePrice?: number;
    /** Resolved from TaxProfile if linked, otherwise same as gstRate */
    resolvedGstRate?: number;
    /** Resolved from TaxProfile if linked, otherwise same as cessRate */
    resolvedCessRate?: number;
}

/** Billing configuration derived from app settings */
export interface BillingConfig {
    isTaxInclusive: boolean;
    roundingMode: RoundingMode;
    isCompositionDealer: boolean;
}

export interface LineItemTax {
    taxableAmountPaisa: number;
    gstRate: number;
    cessRate: number;
    cgstPaisa: number;
    sgstPaisa: number;
    igstPaisa: number;
    cessPaisa: number;
    totalPaisa: number;
}

export interface ComputedLineItem {
    productId: string;
    productName: string;
    hsnCode: string | null;
    unit: string;
    quantity: number;
    unitPrice: number;
    grossAmountPaisa: number;
    discountAmountPaisa: number;
    taxableAmountPaisa: number;
    gstRate: number;
    cessRate: number;
    cgstPaisa: number;
    sgstPaisa: number;
    igstPaisa: number;
    cessPaisa: number;
    totalPaisa: number;
}

export interface InvoiceTotals {
    subtotalPaisa: number;
    globalDiscountPaisa: number;
    taxableAmountPaisa: number;
    cgstPaisa: number;
    sgstPaisa: number;
    igstPaisa: number;
    cessPaisa: number;
    totalTaxPaisa: number;
    preRoundTotalPaisa: number;
    roundOffPaisa: number;
    finalTotalPaisa: number;
    subtotal: number;
    globalDiscount: number;
    taxableAmount: number;
    cgst: number;
    sgst: number;
    igst: number;
    cess: number;
    totalTax: number;
    roundOff: number;
    totalAmount: number;
}

export type PaymentStatus = 'PAID' | 'PARTIAL' | 'UNPAID';

// ═══════════════════════════════════════════════════════
//  UTILITY: Paisa conversions
// ═══════════════════════════════════════════════════════

export function toPaisa(rupees: number): number {
    return Math.round(rupees * 100);
}

export function toRupees(paisa: number): number {
    return Number((Math.round(paisa) / 100).toFixed(2));
}

// ═══════════════════════════════════════════════════════
//  LINE ITEM TAX COMPUTATION
// ═══════════════════════════════════════════════════════

export const DEFAULT_BILLING_CONFIG: BillingConfig = {
    isTaxInclusive: false,
    roundingMode: 'NEAREST',
    isCompositionDealer: false,
};

export function computeLineItemTax(
    quantity: number,
    unitPrice: number,
    discountAmount: number,
    gstRate: number,
    cessRate: number,
    isInterState: boolean,
    isTaxInclusive: boolean = false,
): LineItemTax {
    const grossPaisa = toPaisa(quantity * unitPrice);
    const discountPaisa = toPaisa(discountAmount);
    const afterDiscountPaisa = Math.max(0, grossPaisa - discountPaisa);

    let taxableAmountPaisa: number;

    if (isTaxInclusive) {
        taxableAmountPaisa = reverseCalculateTaxable(afterDiscountPaisa, gstRate, cessRate);
    } else {
        taxableAmountPaisa = afterDiscountPaisa;
    }

    let cgstPaisa = 0;
    let sgstPaisa = 0;
    let igstPaisa = 0;

    if (isInterState) {
        igstPaisa = Math.round(taxableAmountPaisa * gstRate / 100);
    } else {
        cgstPaisa = Math.round(taxableAmountPaisa * gstRate / 200);
        sgstPaisa = Math.round(taxableAmountPaisa * gstRate / 200);
    }

    const cessPaisa = Math.round(taxableAmountPaisa * cessRate / 100);
    const totalPaisa = taxableAmountPaisa + cgstPaisa + sgstPaisa + igstPaisa + cessPaisa;

    return {
        taxableAmountPaisa,
        gstRate,
        cessRate,
        cgstPaisa,
        sgstPaisa,
        igstPaisa,
        cessPaisa,
        totalPaisa,
    };
}

export function computeLineItem(
    product: ProductForBilling,
    quantity: number,
    unitPrice: number,
    discountAmount: number,
    isInterState: boolean,
    config: BillingConfig = DEFAULT_BILLING_CONFIG,
): ComputedLineItem {
    const effectiveGstRate = product.resolvedGstRate ?? product.gstRate;
    const effectiveCessRate = product.resolvedCessRate ?? product.cessRate;

    const finalGstRate = config.isCompositionDealer ? 0 : effectiveGstRate;
    const finalCessRate = config.isCompositionDealer ? 0 : effectiveCessRate;

    const grossAmountPaisa = toPaisa(quantity * unitPrice);
    const discountAmountPaisa = toPaisa(discountAmount);
    const tax = computeLineItemTax(
        quantity,
        unitPrice,
        discountAmount,
        finalGstRate,
        finalCessRate,
        isInterState,
        config.isTaxInclusive,
    );

    return {
        productId: product.id,
        productName: product.name,
        hsnCode: product.hsnCode,
        unit: product.unit,
        quantity,
        unitPrice,
        grossAmountPaisa,
        discountAmountPaisa,
        taxableAmountPaisa: tax.taxableAmountPaisa,
        gstRate: finalGstRate,
        cessRate: finalCessRate,
        cgstPaisa: tax.cgstPaisa,
        sgstPaisa: tax.sgstPaisa,
        igstPaisa: tax.igstPaisa,
        cessPaisa: tax.cessPaisa,
        totalPaisa: tax.totalPaisa,
    };
}

// ═══════════════════════════════════════════════════════
//  INVOICE TOTALS COMPUTATION
// ═══════════════════════════════════════════════════════

export interface InvoiceResult {
    totals: InvoiceTotals;
    adjustedItems: ComputedLineItem[];
}

export function computeInvoiceTotals(
    items: ComputedLineItem[],
    globalDiscountRupees: number = 0,
    isInterState: boolean = false,
    config: BillingConfig = DEFAULT_BILLING_CONFIG,
): InvoiceResult {
    const globalDiscountPaisa = toPaisa(globalDiscountRupees);

    const rawSubtotalPaisa = items.reduce((acc, item) => acc + item.taxableAmountPaisa, 0);

    let runningDiscountTotal = 0;
    let cgstPaisa = 0;
    let sgstPaisa = 0;
    let igstPaisa = 0;
    let cessPaisa = 0;
    let taxableAmountAfterGlobalDiscountPaisa = 0;

    const sortedIndices = items
        .map((_, index) => index)
        .sort((a, b) => items[b].taxableAmountPaisa - items[a].taxableAmountPaisa);

    const adjustedItems: ComputedLineItem[] = new Array(items.length);

    for (let i = 0; i < sortedIndices.length; i++) {
        const idx = sortedIndices[i];
        const item = items[idx];

        let itemDiscountPaisa = 0;
        if (rawSubtotalPaisa > 0) {
            if (i === sortedIndices.length - 1) {
                itemDiscountPaisa = globalDiscountPaisa - runningDiscountTotal;
            } else {
                itemDiscountPaisa = Math.floor((item.taxableAmountPaisa / rawSubtotalPaisa) * globalDiscountPaisa);
                runningDiscountTotal += itemDiscountPaisa;
            }
        }

        const newTaxableAmount = Math.max(0, item.taxableAmountPaisa - itemDiscountPaisa);

        let itemCgst = 0;
        let itemSgst = 0;
        let itemIgst = 0;

        if (isInterState) {
            itemIgst = Math.round((newTaxableAmount * item.gstRate) / 100);
        } else {
            itemCgst = Math.round((newTaxableAmount * item.gstRate) / 200);
            itemSgst = Math.round((newTaxableAmount * item.gstRate) / 200);
        }

        const itemCess = Math.round((newTaxableAmount * item.cessRate) / 100);

        taxableAmountAfterGlobalDiscountPaisa += newTaxableAmount;
        cgstPaisa += itemCgst;
        sgstPaisa += itemSgst;
        igstPaisa += itemIgst;
        cessPaisa += itemCess;

        adjustedItems[idx] = {
            ...item,
            discountAmountPaisa: item.discountAmountPaisa + itemDiscountPaisa,
            taxableAmountPaisa: newTaxableAmount,
            cgstPaisa: itemCgst,
            sgstPaisa: itemSgst,
            igstPaisa: itemIgst,
            cessPaisa: itemCess,
            totalPaisa: newTaxableAmount + itemCgst + itemSgst + itemIgst + itemCess,
        };
    }

    const totalTaxPaisa = cgstPaisa + sgstPaisa + igstPaisa + cessPaisa;
    const preRoundTotalPaisa = taxableAmountAfterGlobalDiscountPaisa + totalTaxPaisa;

    const finalTotalPaisa = applyRounding(preRoundTotalPaisa, config.roundingMode);
    const roundOffPaisa = finalTotalPaisa - preRoundTotalPaisa;

    const totals: InvoiceTotals = {
        subtotalPaisa: rawSubtotalPaisa,
        globalDiscountPaisa,
        taxableAmountPaisa: taxableAmountAfterGlobalDiscountPaisa,
        cgstPaisa,
        sgstPaisa,
        igstPaisa,
        cessPaisa,
        totalTaxPaisa,
        preRoundTotalPaisa,
        roundOffPaisa,
        finalTotalPaisa,
        subtotal: toRupees(rawSubtotalPaisa),
        globalDiscount: toRupees(globalDiscountPaisa),
        taxableAmount: toRupees(taxableAmountAfterGlobalDiscountPaisa),
        cgst: toRupees(cgstPaisa),
        sgst: toRupees(sgstPaisa),
        igst: toRupees(igstPaisa),
        cess: toRupees(cessPaisa),
        totalTax: toRupees(totalTaxPaisa),
        roundOff: toRupees(roundOffPaisa),
        totalAmount: toRupees(finalTotalPaisa),
    };

    return { totals, adjustedItems };
}

// ═══════════════════════════════════════════════════════
//  PAYMENT STATUS DERIVATION
// ═══════════════════════════════════════════════════════

/**
 * Split a tender into what the shop keeps and what it hands back.
 *
 * Cash is the only tender that produces change: ₹500 against a ₹450 bill puts
 * ₹450 in the drawer and ₹50 back in the customer's hand. Recording the full
 * ₹500 as the payment overstated the sale — the cash book sums payment rows
 * with no method filter, so day-close read heavy on every such sale, and on
 * mobile the unclamped negative due quietly reduced the customer's balance.
 *
 * Electronic tenders are charged exactly. An overpay there is a data-entry
 * mistake and the customer really was debited, so it is passed through rather
 * than silently swallowed — the money exists and someone has to refund it.
 */
export function applyTender(
    method: string,
    tenderedRupees: number,
    balanceRupees: number,
): { applied: number; change: number } {
    if (method !== 'CASH') return { applied: tenderedRupees, change: 0 };
    const applied = Math.max(0, Math.min(tenderedRupees, balanceRupees));
    return { applied, change: Math.max(0, tenderedRupees - applied) };
}

/**
 * Recompute one returned line the way the sale computed it.
 *
 * Returns used to credit `quantityToReturn * unitPrice` and then add GST on
 * top, which was wrong twice over:
 *
 *  - It ignored the store's TAX_INCLUSIVE convention. Where prices are entered
 *    inclusive, an item sold for 118 (100 + 18% GST) was credited 118 + 21.24 =
 *    139.24 — the shop refunded tax it never collected.
 *  - It ignored the line's discount, which on a sale already carries both the
 *    item-level discount and its share of the global one. An item sold at 100
 *    less 20 was paid for at 94.40 and refunded 118.
 *
 * A return credits what the customer actually paid for the units coming back,
 * so it runs through the same computeLineItemTax() the sale did, with the
 * discount proportioned to the returned quantity.
 */
/** What the ORIGINAL line recorded, in paisa. The only honest basis for a refund. */
export interface OriginalLineAmounts {
    taxableAmountPaisa: number;
    cgstPaisa: number;
    sgstPaisa: number;
    igstPaisa: number;
    cessPaisa: number;
    totalPaisa: number;
    discountAmountPaisa: number;
}

/**
 * What a return credits back.
 *
 * Reverses the amounts the original sale RECORDED, rather than recomputing them
 * from today's prices and today's tax settings.
 *
 * That distinction is the whole point. The previous version re-ran
 * `computeLineItemTax` using the store's current `TAX_INCLUSIVE` setting, so an
 * item sold for ₹118 inclusive and returned after the shop switched to
 * exclusive pricing was credited ₹118 + ₹21.24 — refunding tax nobody ever
 * collected. The same flip in the other direction short-changed the customer.
 * Nothing about a past sale changes when a setting changes, so nothing about
 * its refund should either.
 *
 * A full return credits exactly what was charged, by construction rather than
 * by arithmetic agreement. A partial return takes its proportional share, with
 * the tax components summed into the total so the parts always reconcile — GST
 * returns are filed on the components, not the total.
 *
 * Note the residual: returning 1 of 3 units three times can land a paisa away
 * from the original total, which is inherent to splitting an indivisible unit.
 * It is bounded by a few paisa per line, where the bug this replaces was
 * measured in rupees.
 */
export function computeReturnLine(args: {
    quantityToReturn: number;
    originalQuantity: number;
    gstRate: number;
    cessRate: number;
    original: OriginalLineAmounts;
}): LineItemTax & { discountAmountPaisa: number } {
    const { quantityToReturn, originalQuantity, gstRate, cessRate, original } = args;

    const empty = {
        taxableAmountPaisa: 0,
        gstRate,
        cessRate,
        cgstPaisa: 0,
        sgstPaisa: 0,
        igstPaisa: 0,
        cessPaisa: 0,
        totalPaisa: 0,
        discountAmountPaisa: 0,
    };

    // A zero or negative original quantity should never reach here; if it does,
    // crediting nothing is safer than dividing by it.
    if (originalQuantity <= 0 || quantityToReturn <= 0) return empty;

    // Returning the whole line gives back exactly what was charged for it — no
    // proportioning, so no rounding can creep in on the common case.
    if (quantityToReturn >= originalQuantity) {
        return {
            taxableAmountPaisa: original.taxableAmountPaisa,
            gstRate,
            cessRate,
            cgstPaisa: original.cgstPaisa,
            sgstPaisa: original.sgstPaisa,
            igstPaisa: original.igstPaisa,
            cessPaisa: original.cessPaisa,
            totalPaisa: original.totalPaisa,
            discountAmountPaisa: original.discountAmountPaisa,
        };
    }

    const share = (paisa: number) => Math.round((paisa * quantityToReturn) / originalQuantity);

    const taxableAmountPaisa = share(original.taxableAmountPaisa);
    const cgstPaisa = share(original.cgstPaisa);
    const sgstPaisa = share(original.sgstPaisa);
    const igstPaisa = share(original.igstPaisa);
    const cessPaisa = share(original.cessPaisa);

    return {
        taxableAmountPaisa,
        gstRate,
        cessRate,
        cgstPaisa,
        sgstPaisa,
        igstPaisa,
        cessPaisa,
        // Summed, not proportioned separately, so the components always add up
        // to the credited total.
        totalPaisa: taxableAmountPaisa + cgstPaisa + sgstPaisa + igstPaisa + cessPaisa,
        discountAmountPaisa: share(original.discountAmountPaisa),
    };
}

/**
 * Would putting `owedRupees` on this customer's khata breach their limit?
 *
 * A credit limit of 0 means "no limit set", not "no credit allowed" — that is
 * the default on every customer row, and treating it as a hard zero would
 * refuse every credit sale in the product.
 *
 * Mobile enforced this from the start and desktop and web never did, so the
 * same customer could run up any balance at a counter but not on a phone. One
 * rule, shared, so they cannot disagree again.
 */
export function exceedsCreditLimit(
    currentBalanceRupees: number,
    creditLimitRupees: number,
    owedRupees: number,
): boolean {
    if (creditLimitRupees <= 0) return false;
    return currentBalanceRupees + owedRupees > creditLimitRupees;
}

export interface SessionCashInput {
    openingCash: number;
    /** CASH payments stamped with this session, with the type of what they paid. */
    payments: { amount: number; invoiceType: string }[];
    /** Expenses booked against this session, with their tender. */
    expenses: { amount: number; paymentMethod: string | null }[];
}

export interface SessionCash {
    openingCash: number;
    cashSales: number;
    cashRefunds: number;
    cashExpenses: number;
    expectedCash: number;
}

/**
 * What the drawer should hold at close.
 *
 *   opening + cash taken in − cash refunded − cash spent
 *
 * The old calculation summed CASH payments on the session's SALE invoices and
 * subtracted the store's expenses for the calendar day. That basis could not
 * express three things a shop does daily:
 *
 *   1. A khata payment collected today against last week's invoice. The money
 *      is in the drawer; the invoice belonged to a closed session, so the count
 *      never saw it.
 *   2. A refund paid out of the drawer. Credit notes were excluded outright, so
 *      cash leaving the till was invisible to the count.
 *   3. Two counters open at once. Expenses were store-wide for the day, so the
 *      same expense was charged against every drawer.
 *
 * Scoping payments and expenses to the session — which is what the
 * billing_session_id columns are for — answers all three. A payment on a
 * CREDIT_NOTE is money going the other way, so it subtracts.
 *
 * An expense with no recorded tender counts as cash: that is what it used to
 * do, and a shopkeeper who did not pick a method at a till was paying from the
 * till.
 */
export function summariseSessionCash(input: SessionCashInput): SessionCash {
    let cashSales = 0;
    let cashRefunds = 0;
    for (const p of input.payments) {
        if (p.invoiceType === 'CREDIT_NOTE') cashRefunds += p.amount;
        else cashSales += p.amount;
    }

    const cashExpenses = input.expenses
        .filter((e) => !e.paymentMethod || e.paymentMethod === 'CASH')
        .reduce((sum, e) => sum + e.amount, 0);

    return {
        openingCash: input.openingCash,
        cashSales,
        cashRefunds,
        cashExpenses,
        expectedCash: input.openingCash + cashSales - cashRefunds - cashExpenses,
    };
}

// ═══════════════════════════════════════════════════════
//  LOYALTY
// ═══════════════════════════════════════════════════════

/**
 * How a store converts spend into points and points back into money.
 *
 * This is a shopkeeper's decision, not a platform's, and it used to be neither:
 * desktop and web hardcoded one point per rupee while mobile hardcoded one per
 * hundred. With redemption fixed at one rupee per point, that made desktop and
 * web hand back the ENTIRE value of every sale as store credit — 100% cashback,
 * on every bill either of them ever rang up.
 */
export interface LoyaltyConfig {
    /** Rupees of spend that earn one point. 0 or less disables earning. */
    rupeesPerPoint: number;
    /** Rupees of discount one point is worth when redeemed. */
    pointValueRupees: number;
}

/**
 * 1 point per 100 spent, each worth 1 — a 1% rate, which is what mobile did and
 * what ordinary retail loyalty looks like. Desktop and web defaulting to 100%
 * was not a policy anyone chose; it was arithmetic nobody compared.
 */
export const DEFAULT_LOYALTY_CONFIG: LoyaltyConfig = {
    rupeesPerPoint: 100,
    pointValueRupees: 1,
};

/**
 * Points earned on a sale.
 *
 * `tierMultiplier` comes from the customer's loyalty tier where the client
 * supports tiers; 1 means no tier bonus. Floors twice, as mobile did: part of a
 * point is not a point, and part of a bonus point is not one either.
 */
export function computeLoyaltyPointsEarned(
    totalRupees: number,
    config: LoyaltyConfig = DEFAULT_LOYALTY_CONFIG,
    tierMultiplier = 1,
): number {
    if (config.rupeesPerPoint <= 0) return 0; // loyalty turned off
    if (totalRupees <= 0) return 0;
    const base = Math.floor(totalRupees / config.rupeesPerPoint);
    return Math.max(0, Math.floor(base * Math.max(0, tierMultiplier)));
}

/** What `points` are worth as a discount, in rupees. */
export function loyaltyRedemptionValue(
    points: number,
    config: LoyaltyConfig = DEFAULT_LOYALTY_CONFIG,
): number {
    if (points <= 0 || config.pointValueRupees <= 0) return 0;
    return points * config.pointValueRupees;
}

/**
 * The most a customer may redeem against this bill.
 *
 * Capped by what they hold and by the bill itself — redeeming past the total
 * would turn a sale into a payout.
 */
export function maxRedeemablePoints(
    availablePoints: number,
    billRupees: number,
    config: LoyaltyConfig = DEFAULT_LOYALTY_CONFIG,
): number {
    if (availablePoints <= 0 || billRupees <= 0 || config.pointValueRupees <= 0) return 0;
    const affordable = Math.floor(billRupees / config.pointValueRupees);
    return Math.max(0, Math.min(Math.floor(availablePoints), affordable));
}

export function derivePaymentStatus(totalAmountPaisa: number, paidAmountPaisa: number): PaymentStatus {
    if (paidAmountPaisa >= totalAmountPaisa) return 'PAID';
    if (paidAmountPaisa > 0) return 'PARTIAL';
    return 'UNPAID';
}

// ═══════════════════════════════════════════════════════
//  INVOICE NUMBER GENERATION
// ═══════════════════════════════════════════════════════

export function generateInvoiceNumber(
    nextInvoiceNo: number,
    prefix?: string | null,
    deviceSlot?: number | null,
): string {
    // Normalise a trailing separator so a store prefix of "INV-" doesn't
    // produce a double dash ("INV--000001"); fall back to INV if it was only
    // separators.
    const pfx = String(prefix ?? '').replace(/[-–—/\s]+$/, '') || 'INV';
    if (deviceSlot != null) {
        const slot = String(deviceSlot).padStart(2, '0');
        return `${pfx}-${slot}${String(nextInvoiceNo).padStart(6, '0')}`;
    }
    return `${pfx}-${String(nextInvoiceNo).padStart(6, '0')}`;
}

// ═══════════════════════════════════════════════════════
//  CURRENCY FORMATTER
// ═══════════════════════════════════════════════════════

const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

export function formatCurrency(amountInRupees: number): string {
    return INR_FORMATTER.format(amountInRupees);
}

export function formatPaisa(paisa: number): string {
    return formatCurrency(toRupees(paisa));
}
